import { useState, useRef, useEffect, type ReactNode } from 'react';
import LatexText from './LatexText';

interface LatexEditorProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  singleLine?: boolean;
  className?: string;
  expandable?: boolean;
  disabled?: boolean;
}

function hasLatex(text: string): boolean {
  if (!text) return false;
  return /\$|\\\(|\\\[|[^\x00-\x7F]/.test(text);
}

/** Shared wrapper that positions expand (top-right) and action (bottom-right) buttons */
function EditorShell({ children, expandButton, actionButton, expandModal, className, onClick }: {
  children: ReactNode;
  expandButton: ReactNode;
  actionButton: ReactNode;
  expandModal: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div className={className} onClick={onClick} style={{ position: 'relative' }}>
      {children}
      {expandButton && (
        <div style={{ position: 'absolute', top: '2px', right: '2px' }}>
          {expandButton}
        </div>
      )}
      {actionButton && (
        <div style={{ position: 'absolute', bottom: '2px', right: '2px' }}>
          {actionButton}
        </div>
      )}
      {expandModal}
    </div>
  );
}

export default function LatexEditor({
  value, onChange, rows = 4, placeholder, singleLine, className, expandable, disabled,
}: LatexEditorProps) {
  const [editing, setEditing] = useState(false);
  const [showExpandModal, setShowExpandModal] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  const smallBtn = (label: string, onClick: (e: React.MouseEvent) => void) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '2px 8px', fontSize: '11px', fontWeight: 500,
        background: 'var(--bg-tertiary, #e5e7eb)', color: 'var(--text-primary, #374151)',
        border: 'none', borderRadius: '4px', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  const editBtn = !disabled ? smallBtn('Edit', e => { e.stopPropagation(); setEditing(true); }) : null;
  const doneBtn = smallBtn('Done', () => setEditing(false));

  const expandBtn = expandable ? (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); setShowExpandModal(true); }}
      title="Expand"
      style={{
        padding: '2px 6px', fontSize: '13px', fontWeight: 500,
        background: 'var(--bg-tertiary)', color: 'var(--text-secondary)',
        border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer',
        lineHeight: 1,
      }}
    >
      &#x2922;
    </button>
  ) : null;

  const expandModal = showExpandModal ? (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)', borderRadius: '10px',
          maxWidth: '900px', width: '90vw', height: '80vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Edit LaTeX</span>
          <button
            type="button"
            onClick={() => setShowExpandModal(false)}
            style={{
              padding: '4px 14px', fontSize: '12px', fontWeight: 600,
              background: '#3b82f6', color: '#fff', border: 'none',
              borderRadius: '4px', cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
              flex: '1 1 55%', padding: '16px', fontSize: '14px',
              border: 'none', borderBottom: '1px solid var(--border-color)',
              outline: 'none', resize: 'none', fontFamily: 'monospace',
              backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)',
              minHeight: 0,
            }}
            placeholder={placeholder}
            disabled={disabled}
          />
          <div style={{
            flex: '1 1 45%', padding: '16px', overflowY: 'auto',
            backgroundColor: 'var(--bg-secondary)', minHeight: 0,
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>Preview</div>
            <LatexText text={value} style={{ fontSize: '16px', lineHeight: '1.7' }} />
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Single-line: input with LaTeX preview toggle
  if (singleLine) {
    const showPreview = hasLatex(value) && !editing;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {showPreview ? (
          <div
            onClick={() => !disabled && setEditing(true)}
            className={className}
            style={{
              flex: 1, padding: '4px 8px', cursor: disabled ? 'default' : 'text',
              backgroundColor: 'var(--bg-secondary, #f9fafb)',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '4px', minHeight: '28px',
              display: 'flex', alignItems: 'center',
            }}
          >
            <LatexText text={value} style={{ fontSize: '13px' }} />
          </div>
        ) : (
          <input
            ref={editing ? inputRef as React.RefObject<HTMLInputElement> : undefined}
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
            placeholder={placeholder}
            className={className}
            style={{ flex: 1 }}
            disabled={disabled}
          />
        )}
        {expandBtn}
        {expandModal}
      </div>
    );
  }

  // Multi-line: three modes sharing the same shell layout

  // 1. LaTeX view mode — rendered preview, click to edit
  if (hasLatex(value) && !editing) {
    return (
      <EditorShell className={className} expandButton={expandBtn} actionButton={editBtn} expandModal={expandModal}
        onClick={() => !disabled && setEditing(true)}
      >
        <div style={{
          padding: '8px 10px',
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          minHeight: `${rows * 1.5}em`,
          color: 'var(--text-primary)',
        }}>
          <LatexText text={value} style={{ fontSize: '14px' }} />
        </div>
      </EditorShell>
    );
  }

  // 2. Edit mode (LaTeX content being edited) — raw textarea with Done
  if (editing) {
    return (
      <EditorShell className={className} expandButton={expandBtn} actionButton={doneBtn} expandModal={expandModal}>
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={rows}
          placeholder={placeholder}
        />
      </EditorShell>
    );
  }

  // 3. Plain textarea (no LaTeX yet) — direct editing with Edit button
  return (
    <EditorShell expandButton={expandBtn} actionButton={editBtn} expandModal={expandModal}>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className={className}
        style={expandable || editBtn ? { paddingRight: '36px' } : undefined}
        disabled={disabled}
      />
    </EditorShell>
  );
}
